// react-magnetic-di - temporary comment, for auto dependency injection in tests
import React, { useEffect, useMemo } from 'react';

import { di } from 'react-magnetic-di';
import { graphql, useFragment } from 'react-relay';

import { AnalyticsContext } from '@atlaskit/analytics-next';

import { useAdjustmentsContextActions } from '../../../controllers/adjustments-context';
import { ExecutionContextProvider } from '../../../controllers/execution-context';
import { useViewType } from '../../../controllers/issue-adjustments/hooks/use-view-type';
import { TenantContextProvider } from '../../../controllers/tenant-context';
import {
	isExecutionContextSupported,
	isTriggerPointSupported,
} from '../../../controllers/view-configuration';

import type { fragment_AtlassianUiModificationsCore_RootFragment$key as UiModificationsRootFragmentKey } from './__generated__/fragment_AtlassianUiModificationsCore_RootFragment.graphql';
import { AppsRenderer } from './apps-renderer';
import { useAppsToRender } from './apps-renderer/use-apps-to-render';
import { type RootFragmentProps, type UiModificationsRootFragmentProps } from './types';
import { useContextCleanup } from './use-contexts-cleanup';
import { useEnrichedExecutionContext } from './use-enriched-execution-context';

/**
 * UI modifications root component with Relay fragment.
 *
 * The purpose is to use it as a part of a parent query.
 */
export const UiModificationsRootFragment = (props: UiModificationsRootFragmentProps) => {
	const executionContext = useEnrichedExecutionContext({
		executionContext: props.executionContext,
	});
	const { executionContextKey, triggerPointKey } = props;

	const viewType = useViewType();
	const { setShouldUiModificationsLoad } = useAdjustmentsContextActions();

	const shouldLoad =
		executionContext &&
		executionContextKey &&
		isTriggerPointSupported(viewType, triggerPointKey) &&
		isExecutionContextSupported(executionContext);

	useEffect(() => {
		// `executionContextKey` has to be the dependency of the useEffect
		// to set the shouldUiModificationsLoad state according to context changes
		// that is represented by the `executionContextKey`
		if (shouldLoad) {
			setShouldUiModificationsLoad(shouldLoad ? 'yes' : 'no');
		}
	}, [setShouldUiModificationsLoad, shouldLoad, executionContextKey]);

	if (!shouldLoad) {
		return null;
	}

	return (
		<RootFragmentWithCleanup
			{...props}
			executionContext={executionContext}
			executionContextKey={executionContextKey}
		/>
	);
};

/**
 * It is separated from
 * - `UiModificationsRootFragment` to run cleanup only when we know that we want to render UIM
 * - `RootFragment` to run cleanup imidiately without awaiting for the new Relay data when context changed
 */
const RootFragmentWithCleanup = (props: RootFragmentProps) => {
	di(AnalyticsContext);

	const {
		cloudId,
		activationId,
		executionContextKey,
		triggerPointKey,
		executionContext,
		numberOfSupportedFields,
		resetFormFieldsCounter,
	} = props;

	useContextCleanup({ executionContextKey, numberOfSupportedFields, resetFormFieldsCounter });

	const tenantContext = useMemo(
		() => ({
			cloudId,
			activationId,
		}),
		[cloudId, activationId],
	);

	const analyticsContextData = useMemo(
		() => ({
			attributes: {
				triggerPointKey,
			},
		}),
		[triggerPointKey],
	);

	return (
		<AnalyticsContext data={analyticsContextData}>
			<ExecutionContextProvider value={executionContext}>
				<TenantContextProvider value={tenantContext}>
					<RootFragment {...props} />
				</TenantContextProvider>
			</ExecutionContextProvider>
		</AnalyticsContext>
	);
};

// TODO remove unused props from types and usages
const RootFragment = ({
	fragmentRef,
	ForgeAppRenderer,
	executionContextKey,
}: RootFragmentProps) => {
	di(AppsRenderer);

	const response = useFragment<UiModificationsRootFragmentKey>(
		/* eslint-disable @atlassian/relay/unused-fields */
		graphql`
			fragment fragment_AtlassianUiModificationsCore_RootFragment on Query
			@argumentDefinitions(
				forgeExtensionRenderingContext: { type: "JiraExtensionRenderingContextInput!" }
				uiModificationsContext: { type: "JiraUiModificationsContextInput!" }
				cloudId: { type: "ID!" }
				includeEgressOverridesForge: { type: "Boolean!" }
			) {
				jira {
					uiModifications(context: $uiModificationsContext) @required(action: NONE) {
						appEnvId
						uiModifications {
							id
							data
						}
					}
					forge {
						uiModificationsExtensions: extensions(
							context: $forgeExtensionRenderingContext
							cloudId: $cloudId
							types: ["jira:uiModifications"]
						) @required(action: NONE) {
							id
							appVersion
							installationId
							environmentId
							environmentType
							environmentKey
							license {
								active
								type
								supportEntitlementNumber
								trialEndDate
								subscriptionEndDate
								isEvaluation
								billingPeriod
								ccpEntitlementId
								ccpEntitlementSlug
								capabilitySet
							}
							properties
							scopes
							type
							egress {
								type
								addresses
								category @include(if: $includeEgressOverridesForge)
								inScopeEUD @include(if: $includeEgressOverridesForge)
							}
							overrides @include(if: $includeEgressOverridesForge)
							installationConfig @include(if: $includeEgressOverridesForge) {
								key
								value
							}
							consentUrl
							userAccess {
								hasAccess
							}
							hiddenBy
						}
					}
				}
			}
		`,
		/* eslint-enable @atlassian/relay/unused-fields */
		fragmentRef,
	);

	const appsToRender = useAppsToRender({ data: response, executionContextKey });

	return <AppsRenderer apps={appsToRender} ForgeAppRenderer={ForgeAppRenderer} />;
};
